import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  EXAMINATION_SESSION_FEATURE_KEY,
  ExaminationSessionState,
  examinationSessionAdapter,
} from './examination-session.reducer';
import { selectAllAnswers } from '../answer/answer.selectors';
import { ExaminationSessionAnswersVM } from '@lyceo/lyceo-pupil-app/practise/util-models';
import { CallState, getError, LoadingState } from '@lyceo/shared/util-loading';
import { selectParamExercisesQuestionnaireIds } from '../exercise/exercise.selectors';

export const selectExaminationSessionState =
  createFeatureSelector<ExaminationSessionState>(
    EXAMINATION_SESSION_FEATURE_KEY,
  );

const { selectAll, selectEntities } = examinationSessionAdapter.getSelectors();

export const selectAllExaminationSessions = createSelector(
  selectExaminationSessionState,
  (state: ExaminationSessionState) => selectAll(state),
);

export const selectExaminationSessionEntities = createSelector(
  selectExaminationSessionState,
  (state: ExaminationSessionState) => selectEntities(state),
);

export const selectSelectedId = createSelector(
  selectExaminationSessionState,
  (state: ExaminationSessionState) => state.selectedId,
);

export const selectSelectedExaminationSession = createSelector(
  selectExaminationSessionEntities,
  selectSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined),
);

export const selectLoadedQuestionnaireIds = createSelector(
  selectExaminationSessionState,
  (state: ExaminationSessionState) => state.loadedQuestionnaireIds,
);

export const selectParamExaminationSessionsLoaded = createSelector(
  selectParamExercisesQuestionnaireIds,
  selectLoadedQuestionnaireIds,
  (questionnaireIds, loadedQuestionnaireIds) =>
    questionnaireIds.every((questionnaireId) =>
      loadedQuestionnaireIds.includes(questionnaireId),
    ),
);

export const selectDetailedCallState = createSelector(
  selectExaminationSessionState,
  (state: ExaminationSessionState) => state.detailedCallState,
);

export const selectDetailedLoading = createSelector(
  selectDetailedCallState,
  (callState: CallState) => callState === LoadingState.LOADING,
);

export const selectDetailedError = createSelector(
  selectDetailedCallState,
  (callState: CallState) => getError(callState),
);

export const selectExaminationSessionAnswersVM = createSelector(
  selectDetailedLoading,
  selectDetailedError,
  selectSelectedExaminationSession,
  selectAllAnswers,
  (
    loading,
    error,
    examinationSession,
    answers,
  ): ExaminationSessionAnswersVM => {
    return {
      loading,
      error: !!error,
      examinationSession,
      answers: answers.filter(
        (answer) => answer.examinationSessionId === examinationSession?.id,
      ),
    };
  },
);
